/**
 * Invoice & Payment Tracking workspace.
 *
 * Screens 1 to 3 of six. The remaining three — retention register, connector
 * console and vendor payment performance — are tabs here once built; the
 * placeholders say so rather than pretending to be empty states.
 *
 * A vendor principal does not land on the portfolio dashboard. Pending value,
 * the stage funnel and the cash-out obligation are client-internal treasury
 * figures; a vendor gets their own register with a four-figure strip.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, ChevronRight, FileSpreadsheet, LayoutDashboard, Landmark, Loader2, Users } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import { useIptTheme } from '@/components/ipt/iptTheme';
import { Empty, Panel, SimBadge } from '@/components/ipt/IptPrimitives';
import { IptPrefsBar } from '@/components/ipt/IptPrefsBar';
import IptDashboard from '@/components/ipt/IptDashboard';
import IptRegister from '@/components/ipt/IptRegister';
import IptVendorHome from '@/components/ipt/IptVendorHome';
import IptExplorer from '@/components/ipt/IptExplorer';
import IptRetention from '@/components/ipt/IptRetention';
import IptVendorPerformance from '@/components/ipt/IptVendorPerformance';

const CLIENT_TABS = [
  ['analytics', 'Invoice Analytics', BarChart3],
  ['dashboard', 'Pending Payment', LayoutDashboard],
  ['register', 'Invoice Register', FileSpreadsheet],
  ['vendors', 'Vendor Performance', Users],
  ['retention', 'Retention Register', Landmark],
];
const VENDOR_TABS = [
  ['register', 'My Invoices', FileSpreadsheet],
  ['vendors', 'My Scorecard', Users],
];

export default function InvoiceManagement() {
  const T = useIptTheme();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { projectId, projectName } = useProject();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/ipt/meta${projectId ? `?project_id=${projectId}` : ''}`)
      .then((r) => setMeta(r.data)).catch(() => setMeta(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  const isVendor = Boolean(meta?.principal?.isVendor);
  const tabs = isVendor ? VENDOR_TABS : CLIENT_TABS;
  const tab = useMemo(() => {
    const want = params.get('tab');
    if (tabs.some(([k]) => k === want)) return want;
    return isVendor ? 'register' : 'analytics';
  }, [params, tabs, isVendor]);

  const go = (next) => {
    if (next.invoice) {
      // leave the current filter state in the address bar so the browser's own
      // back button restores the filtered register rather than resetting it
      navigate(`/project-controls/cost/invoices/${next.invoice}?tab=lifecycle`);
      return;
    }
    const sp = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) sp.set(k, String(v)); });
    if (!sp.get('tab')) sp.set('tab', tab);
    setParams(sp);
  };

  const setTab = (k) => {
    const sp = new URLSearchParams();
    sp.set('tab', k);
    setParams(sp);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-28 text-center" data-testid="ipt-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="invoice-payment-tracking"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        <header className="sticky top-14 z-20 -mx-6 px-6 py-3"
          style={{ background: T.page, borderBottom: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-1.5 text-[10.5px] mb-1" style={{ color: T.muted }}>
            <span>Project Controls</span>
            <ChevronRight className="w-3 h-3" />
            <span>Cost</span>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: T.body }}>Invoice &amp; Payment Tracking</span>
            <ChevronRight className="w-3 h-3" />
            <span className="px-1.5 py-0.5 rounded" data-testid="ipt-active-project"
              style={{ background: T.tint(T.primary), color: T.primary }}>
              {projectName || projectId || 'all projects'}
            </span>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <h1 className="text-[22px] font-bold leading-tight"
                style={{ color: T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>
                {isVendor ? 'My Invoices' : 'Invoice & Payment Tracking'}
              </h1>
              <p className="text-[11.5px] mt-0.5 max-w-[760px]" style={{ color: T.body }}>
                {isVendor
                  ? 'Your submitted invoices, where each one sits in our approval chain, and '
                  + 'what we are holding in retention.'
                  : 'A seven-stage lifecycle from receipt to payment, with every time figure '
                  + 'derived from the transition table at read time — never stored, so a '
                  + 'backdated correction changes every number that depends on it.'}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <SimBadge source={meta?.vimSource} T={T} />
              <span className="px-2 py-0.5 rounded-full text-[10px]" data-testid="ipt-my-roles"
                style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, color: T.body }}>
                {(meta?.principal?.roles || []).join(' · ') || 'no invoice role assigned'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <nav className="flex items-center gap-1 flex-wrap">
              {tabs.map(([key, label, Icon]) => (
                <button key={key} type="button" onClick={() => setTab(key)}
                  data-testid={`ipt-tab-${key}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-semibold transition-colors"
                  style={tab === key
                    ? { background: T.primary, color: '#fff' }
                    : { background: 'transparent', color: T.body, border: `1px solid ${T.border}` }}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </nav>
            <div className="ml-auto"><IptPrefsBar /></div>
          </div>
        </header>

        <div className="pb-12">
          {!meta && (
            <Empty T={T} testId="ipt-meta-error">
              The invoice module did not respond. Reload, or check that your account has an
              invoice role.
            </Empty>
          )}
          {meta && tab === 'analytics' && !isVendor && (
            <IptExplorer projectId={projectId} vimSource={meta.vimSource} />
          )}
          {meta && tab === 'dashboard' && !isVendor && (
            <IptDashboard projectId={projectId} vimSource={meta.vimSource} go={go} />
          )}
          {meta && tab === 'register' && (
            <div className="space-y-4">
              {isVendor && <IptVendorHome projectId={projectId} meta={meta} />}
              <IptRegister projectId={projectId} params={params} setParams={setParams}
                vimSource={meta.vimSource} />
            </div>
          )}
          {meta && tab === 'vendors' && (
            <IptVendorPerformance projectId={projectId} isVendor={isVendor} />
          )}
          {meta && tab === 'retention' && (
            <IptRetention projectId={projectId} isVendor={isVendor} />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

